import { AccountRecord, FirmRecord } from '@tassat/fred-definitions';
import { WalletRecordMapped } from '@tassat/fred-extended-definitions';
import { ActionTypes, SelectOption } from '@tassat/ui-definitions';
import { Dispatch } from 'react';

import { IErrors, IFormValues } from './types';
import { FieldValidationAction, FieldValidationState } from '../helpers/fieldValidationReducer';
import {
    textFieldCharacterWhitelist,
    validateEmailFormatting,
    validateNoExcelFormulaStartingCharacters,
    validateNoNonPrintable,
    validatePcgAccountMapping
} from '../helpers/validationHelpers';

export const validateWalletForm = async (values: IFormValues, options: {
    accessControlState: FieldValidationState<SelectOption>;
    actionType: ActionTypes;
    checkPendingTransactions: (
        EOAAddress: string,
        dispatchAccessControlAction: Dispatch<FieldValidationAction<SelectOption>>
    ) => Promise<string>;
    editWallet?: WalletRecordMapped;
    dispatchAccessControlAction: Dispatch<FieldValidationAction<SelectOption>>;
    initialValues: IFormValues;
    firm: FirmRecord;
    firmWallets: Array<AccountRecord>;
}): Promise<IErrors> => {
    const {
        accessControlState,
        actionType,
        checkPendingTransactions,
        editWallet,
        dispatchAccessControlAction,
        initialValues,
        firm,
        firmWallets
    } = options;

    const errors = {} as IErrors;

    const accountName = values.AccountName.trim();
    const initialAccountName = initialValues.AccountName.trim();

    if (!accountName) {
        errors.AccountName = 'Required';
    } else if (actionType === ActionTypes.add || (actionType === ActionTypes.edit && accountName !== initialAccountName)) {
        const doesWalletAlreadyExist = firmWallets.some((wallet) => wallet.AccountName.toLowerCase() === accountName.toLowerCase());
        const walletNameExcelError = validateNoExcelFormulaStartingCharacters(accountName);

        if (doesWalletAlreadyExist) errors.AccountName = 'Account name already exists';
        else if (walletNameExcelError) errors.AccountName = walletNameExcelError;

        const firmNameWhitelistCheckError = textFieldCharacterWhitelist(accountName);

        if (firmNameWhitelistCheckError) errors.AccountName = firmNameWhitelistCheckError;
    }

    if (!values.LinkedAccount.value) {
        errors.LinkedAccount = 'Required';
    }

    const acgNumbersError = validatePcgAccountMapping(values.ACGMapping, 'ACG', 'Wallet');
    if (acgNumbersError) {
        errors.ACGMapping = acgNumbersError;
    }

    const emailFormattingError = validateEmailFormatting(values.Email, true);
    const emailExcelError = validateNoExcelFormulaStartingCharacters(values.Email);
    const emailNonPrintableError = validateNoNonPrintable(values.Email);

    if (emailFormattingError) {
        errors.Email = emailFormattingError;
    } else if (emailNonPrintableError) {
        errors.Email = emailNonPrintableError;
    } else if (emailExcelError) {
        errors.Email = emailExcelError;
    }

    const notes = values.Notes?.trim();
    const notesExcelError = validateNoExcelFormulaStartingCharacters(notes);

    if (notesExcelError) errors.Notes = notesExcelError;

    const notesWhitelistCheckError = textFieldCharacterWhitelist(notes);

    if (notesWhitelistCheckError) errors.Notes = notesWhitelistCheckError;

    if (!accessControlState.validated &&
        actionType === ActionTypes.edit &&
        initialValues.AccessControl.value !== values.AccessControl.value
    ) {
        try {
            const pendingTransactionError = await checkPendingTransactions(editWallet?.wallet.Account || '', dispatchAccessControlAction);
            if (pendingTransactionError) {
                dispatchAccessControlAction({
                    type: 'SERVER_ERROR',
                    payload: { error: pendingTransactionError }
                });
                errors.AccessControl = pendingTransactionError;
            }
        } catch (error) {
            errors.AccessControl = 'Could not validate pending transactions, please try again';
        }
    }

    return errors;
};